import { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import differenceInSeconds from 'date-fns/difference_in_seconds';
import Text, { TEXT_COLOR } from '@components/Text';
import {
  Wrapper,
  Title,
  TitleWrapper,
  PollBodyWrapper,
  NextButtonWrapper,
} from './styles';
import { openPopUp as openPopUpAction } from '@containers/App/containers/PopUp/actions';
import POPUP_TYPES from '@containers/App/containers/PopUp/types';
import { ROUTES } from '@shared/constants';
import { savePoll as savePollAction } from './actions';
import { editPoll as editPollAction } from './actions';
import { getCourses } from '@containers/User/containers/CourseRouter/actions';
import { withTranslation } from 'react-i18next';
import SurveyJsPollRuntime from './SurveyJsPollRuntime';
import Button from '@components/Button';
import { Tooltip } from '@components/Tooltip';
import { moveUserToNextActivity } from './services';
import CTAButton from '@components/CTAButton';

export class PollClient extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isResetting: true,
      isEditing: false,
      isMovingToNextActivity: false,
    };

    this.surveyRuntimeRef = createRef();
  }

  handleEditToggle = () => {
    if (this.state.isEditing) {
      this.setState({ isEditing: false }, () => {
        this.surveyRuntimeRef.current?.reset();
      });
      return;
    }

    this.setState({ isEditing: true });
  };

  setContext(context) {
    this.setState({ context });
  }

  componentDidMount() {
    this.resetPoll();
  }

  handleSurveyComplete = (surveyResponse, pollSkipped = false) => {
    const { startTime, isEditing } = this.state;
    const { savePoll, editPoll, savePollOverride, t, poll, skillParts } = this.props;
    const timeSpent = differenceInSeconds(new Date(), startTime);
    const pollToSave = {
      context: this.generatePollContext(),
      timeSpent,
      surveyResponse,
      pollSkipped,
    };
    if (isEditing) {
      editPoll({ id: poll.activity.id, surveyResponse });
    } else {
      if (savePollOverride) {
        savePollOverride(pollToSave);
      } else {
        savePoll(pollToSave);
      }
    }

    if (pollSkipped) {
       return;
    }

    const currentSkillPartIndex = skillParts?.findIndex((child) => child.id === poll.id);
    const isLastSkillPart = currentSkillPartIndex === skillParts.length - 1;

    this.props.openPopUp(POPUP_TYPES.ALERT, {
      titleText: t('Informative.Positive.questionnaire'),
      bodyText: t('Informative.Positive.questionnaireSaved'),
      buttonText: isLastSkillPart ? t('Actions.Navigation.backToOverview') : t('Actions.Navigation.next'),
      onClose: () => this.next({ justSaved: true }),
      hideCloseIcon: true,
      disableBackdrop: true,
    });
  };

  handleSkipPoll = () => {
    const { t } = this.props;
    this.props.openPopUp(POPUP_TYPES.CONFIRM, {
      hideCloseIcon: true,
      disableBackdrop: true,
      titleText: t('Poll.skipConfirm'),
      bodyText: t('Poll.skipConfirmDescription'),
      cancelText: t('Actions.Instance.cancel'),
      confirmText: t('Poll.skipQuestions'),
      onConfirm: () => this.handleSurveyComplete(undefined, true),
      onClose: this.redirectAfterSave
    });
  };

  generatePollContext() {
    const {
      poll: {
        id,
        version,
      },
      activityContext,
    } = this.props;
    const context = activityContext ? _.clone(activityContext) : {};

    context.POLL = { id, version };
    this.setContext(context);
    return context;
  }

  redirectToOverview = () => {
    const { history } = this.props;
    history.push(ROUTES.COURSE_PAGE);
  };

  redirectToNextSkillPart = () => {
    const {
      poll,
      history,
      skillParts,
      match: {
        params: { courseIndex, skillIndex },
      },
    } = this.props;
    const currentSkillPartIndex = skillParts?.findIndex((child) => child.id === poll.id);
    if (!skillParts || currentSkillPartIndex === skillParts.length - 1 || currentSkillPartIndex === -1 || courseIndex === undefined || skillIndex === undefined) {
      this.redirectToOverview();
      return;
    }
    const nextSkillPart = skillParts[currentSkillPartIndex + 1];
    const nextSkillPartType = nextSkillPart.type.toLowerCase();
    const nextSkillPartIndex = nextSkillPart.skillPartIndex;
    const nextPartPath = `${ROUTES.COURSE_PAGE}/${courseIndex}/${skillIndex}/${nextSkillPartType}${nextSkillPartIndex}`;
    history.push(nextPartPath, { nextPath: true });
  };

  redirectAfterSave = async () => {
    const { dispatchGetCourses } = this.props;

    this.redirectToNextSkillPart()
    await dispatchGetCourses();
  };

  isCurrentNextActivity = () => {
    const { poll, currentCourseActivity } = this.props;

    return currentCourseActivity?.entityType === 'POLL'
      && currentCourseActivity?.entityId === poll.id;
  };

  next = async ({ justSaved = false } = {}) => {
    const { poll, skillParts, userId } = this.props;
    if ((!poll?.activity && !justSaved) || this.state.isMovingToNextActivity) return null;

    const currentSkillPartIndex = skillParts?.findIndex((child) => child.id === poll.id);
    const isLastSkillPart = !skillParts || currentSkillPartIndex === skillParts.length - 1 || currentSkillPartIndex === -1;
    if (isLastSkillPart) {
      this.redirectToOverview();
      return null;
    }

    if (!this.isCurrentNextActivity()) {
      this.redirectToNextSkillPart();
      return null;
    }

    if (!userId) return null;

    this.setState({ isMovingToNextActivity: true });
    try {
      const nextActivity = await moveUserToNextActivity(String(userId));
      if (nextActivity === undefined) return null;

      this.redirectAfterSave();
      return nextActivity;
    } finally {
      this.setState({ isMovingToNextActivity: false });
    }
  };

  backToCoursePage = () => {
    const { history } = this.props;
    // will be used to dispatchGetCourses when going back to overview
    history.push(ROUTES.COURSE_PAGE, { getCourses: 'getCourses' });
  };

  resetPoll = () => {
    this.setState({ isResetting: true }, () => {
      const startTime = new Date();
      this.setState({
        isResetting: false,
        startTime,
      });
    });
  };

  renderTitle = (poll) => (
    <TitleWrapper>
      <Title>
        <Text size='T3' textColor={TEXT_COLOR.DISABLED} weight='900'>
          {poll.description}
        </Text>
      </Title>
      {this.props.poll?.activity?.pollSkipped && !this.state.isEditing && (
        <Text size="T3" style={{ alignSelf: 'end' }} textColor={TEXT_COLOR.DARK_BLUE} weight="500">
          {this.props.t('Poll.skippedDescription')}
        </Text>
      )}
      {!!this.props.poll.activity && (
        <Tooltip
          content={this.props.poll?.feedback?.length
            ? this.props.t('Informative.Demanding.pollAlreadyViewed')
            : ''}
          placement="top"
          hasArrow
        >
          <span>
            <Button
              onClick={this.handleEditToggle}
              cta={!this.state.isEditing}
              disabled={!!this.props.poll?.feedback?.length}
              style={{ width: 'auto' }}
            >
              {this.props.t(`Actions.Instance.${this.state.isEditing ? 'cancel' : 'update'}`)}
            </Button>
          </span>
        </Tooltip>
      )}
      {!this.props.poll?.activity && this.props.poll?.skipPollEnabled && (
        <Button cta onClick={this.handleSkipPoll} style={{ width: 'auto' }}>
          {this.props.t(`Poll.skipQuestions`)}
        </Button>
      )}
    </TitleWrapper>
  );

  render() {
    const {
      poll,
    } = this.props;
    const isSurveySchemaPoll = poll && poll.surveySchema && Array.isArray(poll.surveySchema.pages);
    const canEditPoll = !!poll.activity && !poll?.feedback?.length && this.state.isEditing;
    const showNextButton = !!poll.activity && !this.state.isEditing;

    return (
      <Wrapper>
        {this.renderTitle(poll)}
        <PollBodyWrapper>
          {isSurveySchemaPoll ? (
            <SurveyJsPollRuntime
              ref={this.surveyRuntimeRef}
              schema={poll.surveySchema}
              readOnly={!!poll.activity && !canEditPoll}
              data={poll.activity?.surveyResponse}
              onComplete={this.handleSurveyComplete}
            />
          ) : null}
        </PollBodyWrapper>
        {showNextButton && (
          <NextButtonWrapper>
            <CTAButton
              cta
              disabled={this.state.isMovingToNextActivity}
              onClick={this.next}
            >
              {this.props.t(`Actions.Navigation.next`)}
            </CTAButton>
          </NextButtonWrapper>
        )}
      </Wrapper>
    );
  }
}

PollClient.propTypes = {
  poll: PropTypes.object,
  activityContext: PropTypes.object,
  openPopUp: PropTypes.func,
  savePoll: PropTypes.func,
  history: PropTypes.object,
  savePollOverride: PropTypes.func,
  skillParts: PropTypes.array,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentCourseActivity: PropTypes.object,
};

export const mapStateToProps = (state) => {
  const res = {};
  if (state.getIn(['currentPoll'])) {
    res.poll = state.getIn(['currentPoll']).toJS().poll;
  }
  if (state.getIn(['course'])) {
    const courseState = state.getIn(['course']).toJS();
    const currentCourse = courseState.courses.find((course) => course.nextCourseActivity);
    res.activityContext = courseState.activityContext;
    res.currentCourseActivity = currentCourse?.nextCourseActivity;
  }
  if (state.getIn(['app', 'authentication', 'user'])) {
    res.userId = state.getIn(['app', 'authentication', 'user', 'id']);
  }

  return { ...res };
};

export function mapDispatchToProps(dispatch) {
  return {
    openPopUp: (type, props) => dispatch(openPopUpAction(type, props)),
    savePoll: (poll) => dispatch(savePollAction(poll)),
    editPoll: (poll) => dispatch(editPollAction(poll)),
    dispatchGetCourses: () => dispatch(getCourses()),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
});

export default compose(withRouter, withConnect, withTranslation())(PollClient);
